import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { Box, Typography, CircularProgress, Button, Alert, Chip, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';

import { FreeMode, Navigation } from 'swiper/modules';
import { TournamentCard } from './TournamentCard';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppState } from '../store';

export const TournamentListView = ({ setId }: any) => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();

    const userModule = useSelector((state: AppState) => state.userModule);
    const loginInfo = userModule.loginInfo;
    const gameStatus = userModule.gameStatus;
    const [isLoading, setIsLoading] = useState(true);

    const sampleTournaments: React.SetStateAction<any[]> = [];

    const [allTournaments, setAllTournaments] = useState<Array<any>>([]);
    const [filteredTournaments, setFilteredTournaments] = useState<Array<any>>([]);
    const [currentFilter, setCurrentFilter] = useState<string>("all");

    useEffect(() => {
        const tournaments = gameStatus?.tournaments?.length ? gameStatus.tournaments : [];
        setAllTournaments(tournaments);
        setFilteredTournaments(tournaments);
        setIsLoading(false);
    }, [JSON.stringify(gameStatus?.tournaments)]);

    const handleFilterChange = (filterValue: string) => {
        setCurrentFilter(filterValue);
        
        let filtered = allTournaments;
        if (filterValue === "entered") {
            filtered = allTournaments.filter((t: any) => t.is_entered);
        } else if (filterValue === "active") {
            filtered = allTournaments.filter((t: any) => t.is_open);
        } else if (filterValue === "ended") {
            filtered = allTournaments.filter((t: any) => !t.is_open);
        }
        
        setFilteredTournaments(filtered);
    };

    const resetFilter = () => {
        setCurrentFilter("all");
        setFilteredTournaments(allTournaments);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#e94c58' }} />
            </Box>
        );
    }

    if (allTournaments.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h5" color="white" gutterBottom>
                    No Active Tournaments
                </Typography>
                <Typography variant="body1" color="rgba(255,255,255,0.7)" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
                    There are no tournaments available at the moment. Check back later for upcoming events.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Alert severity="info" sx={{
                mb: 3,
                backgroundColor: 'rgba(41, 182, 246, 0.15)',
                color: 'white',
                border: '1px solid rgba(41, 182, 246, 0.3)',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                letterSpacing: 0.5,
                '& .MuiAlert-icon': { color: '#29b6f6' }
            }}>
                🎯 Enter tournaments to compete with your <b>Deer NFTs</b> and win <b>ANTLERS</b> rewards! <span role="img" aria-label="antlers">🦌</span>
            </Alert>
            
            {/* Enhanced Filter Section */}
            <Box sx={{ 
                mb: 3, 
                p: 2, 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(67, 40, 24, 0.8) 0%, rgba(153, 88, 42, 0.6) 100%)',
                border: '1px solid rgba(233, 76, 88, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterListIcon sx={{ color: '#e94c58', fontSize: 24 }} />
                        <Typography variant="h6" color="white" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                            Tournament Filters
                        </Typography>
                    </Box>
                    {currentFilter !== "all" && (
                        <IconButton 
                            onClick={resetFilter}
                            sx={{ 
                                color: '#e94c58',
                                backgroundColor: 'rgba(233, 76, 88, 0.1)',
                                border: '1px solid rgba(233, 76, 88, 0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(233, 76, 88, 0.2)',
                                    transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <ClearIcon />
                        </IconButton>
                    )}
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {[
                        { value: "all", label: "All Tournaments", icon: "🏆", count: allTournaments.length },
                        { value: "entered", label: "Already Entered", icon: "✅", count: allTournaments.filter((t: any) => t.is_entered).length },
                        { value: "active", label: "Active Tournaments", icon: "🔥", count: allTournaments.filter((t: any) => t.is_open).length },
                        { value: "ended", label: "Tournaments Ended", icon: "🏁", count: allTournaments.filter((t: any) => !t.is_open).length },
                    ].map((option) => (
                        <Chip
                            key={option.value}
                            label={`${option.icon} ${option.label} (${option.count})`}
                            onClick={() => handleFilterChange(option.value)}
                            sx={{
                                backgroundColor: currentFilter === option.value 
                                    ? 'linear-gradient(135deg, #e94c58 0%, #c41e3a 100%)' 
                                    : 'rgba(255, 255, 255, 0.1)',
                                color: currentFilter === option.value ? 'white' : 'rgba(255, 255, 255, 0.8)',
                                border: currentFilter === option.value 
                                    ? '2px solid #e94c58' 
                                    : '1px solid rgba(233, 76, 88, 0.3)',
                                fontWeight: currentFilter === option.value ? 600 : 500,
                                fontSize: '0.9rem',
                                padding: '8px 16px',
                                height: 'auto',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: currentFilter === option.value 
                                        ? 'linear-gradient(135deg, #c41e3a 0%, #a01830 100%)' 
                                        : 'rgba(233, 76, 88, 0.2)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(233, 76, 88, 0.3)'
                                },
                                '& .MuiChip-label': {
                                    padding: '4px 8px'
                                }
                            }}
                        />
                    ))}
                </Box>
                
                {currentFilter !== "all" && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ fontSize: '0.85rem' }}>
                            Showing {filteredTournaments.length} of {allTournaments.length} tournaments
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* No Results Message */}
            {filteredTournaments.length === 0 && currentFilter !== "all" && (
                <Box sx={{ 
                    textAlign: 'center', 
                    py: 4, 
                    px: 3,
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(233, 76, 88, 0.2)',
                    mb: 3
                }}>
                    <Typography variant="h6" color="white" gutterBottom sx={{ fontWeight: 600 }}>
                        No tournaments match your filter
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ mb: 2 }}>
                        Try selecting a different filter or reset to see all tournaments.
                    </Typography>
                    <Button 
                        variant="outlined" 
                        onClick={resetFilter}
                        sx={{
                            color: '#e94c58',
                            borderColor: '#e94c58',
                            '&:hover': {
                                borderColor: '#c41e3a',
                                backgroundColor: 'rgba(233, 76, 88, 0.1)'
                            }
                        }}
                    >
                        Reset Filter
                    </Button>
                </Box>
            )}

            <Swiper
                slidesPerView={window.innerWidth < 900 ? 1 : window.innerWidth < 1400 ? 2 : 3}
                spaceBetween={24}
                pagination={{ clickable: true }}
                modules={[Pagination]}
                className="mySwiper"
                style={{
                    width: "100%",
                    padding: window.innerWidth < 600 ? "12px 0" : "20px 0",
                    overflow: "hidden",
                    borderRadius: window.innerWidth < 600 ? "8px" : "16px",
                    background: "linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(233,76,88,0.08) 100%)",
                    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                    display: 'flex',
                    alignItems: 'stretch'
                }}
                centeredSlides={filteredTournaments.length === 1}
            >
                {filteredTournaments.map((tournament, idx) => (
                    <SwiperSlide
                        key={`${tournament.id}-${idx}`}
                        style={{
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            // height: window.innerWidth < 600 ? '340px' : '500px',
                            // minHeight: window.innerWidth < 600 ? '340px' : '420px',
                            // maxHeight: window.innerWidth < 600 ? '340px' : '500px',
                            width: window.innerWidth < 600 ? '90vw' : '360px',
                            minWidth: window.innerWidth < 600 ? '90vw' : '360px',
                            maxWidth: window.innerWidth < 600 ? '90vw' : '360px',
                        }}
                    >
                        <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'stretch'
                        }}>
                            <TournamentCard
                                id={tournament.id}
                                details={tournament}
                                is_entered={tournament.is_entered}
                                start_time={tournament.start_time}
                                end_time={tournament.end_time}
                                winner1={tournament.winner1}
                                winner2={tournament.winner2}
                                setId={setId}
                                is_open={tournament.is_open}
                                is_started={tournament.is_started}
                            />
                        </Box>
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>
    );
};